import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getBookProblems, startRender, type NarrationProblem } from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import FailedPassage from "./FailedPassage";

/**
 * NarrationProblems is the book's Needs attention list (ADR-0120): every
 * passage with no audio, by chapter in reading order, as its words — so an
 * author can find the ones a part number never located, and fix them.
 *
 * Nothing here spends. Try again is the chapter's own retry, which re-records
 * only its failed parts through the budget as it always has.
 */
export default function NarrationProblems({ bookID }: { bookID: string }) {
  const t = useT();
  const problems = useQuery({
    queryKey: ["narration-problems", bookID],
    queryFn: () => getBookProblems(bookID),
    retry: false,
    staleTime: 0,
  });

  if (problems.isPending) return <div aria-busy="true" className="h-24" />;
  if (problems.error) {
    return (
      <p className="text-sm text-amber-700 dark:text-amber-400">{t.narration.problemsFailed}</p>
    );
  }
  const chapters = byChapter(problems.data);
  if (chapters.length === 0) {
    return (
      <p data-testid="problems-none" className="text-sm text-muted">
        {t.narration.problemsNone}
      </p>
    );
  }
  return (
    <div data-testid="narration-problems">
      <p className="mb-6 text-sm text-muted">{t.narration.problemsNote}</p>
      <ul className="space-y-8">
        {chapters.map((c) => (
          <ChapterProblems key={c.chapterID} bookID={bookID} {...c} />
        ))}
      </ul>
    </div>
  );
}

function ChapterProblems({
  bookID,
  chapterID,
  title,
  failed,
  items,
}: {
  bookID: string;
  chapterID: string;
  title: string;
  failed: boolean;
  items: NarrationProblem[];
}) {
  const t = useT();
  const client = useQueryClient();
  const retryable = items.filter((p) => p.retryable).length;
  const retry = useMutation({
    mutationFn: () => startRender(chapterID),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["narration-problems", bookID] });
      await client.invalidateQueries({ queryKey: ["latest-render", chapterID] });
    },
  });
  return (
    <li data-testid="problem-chapter">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <Link
          to={`/studio/chapters/${chapterID}`}
          dir="auto"
          className="font-medium text-ink hover:underline"
        >
          {title}
        </Link>
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {failed
            ? t.narration.problemsChapterFailed
            : t.narration.problemsChapterMissing(items.length)}
        </span>
      </div>
      <ul className="space-y-4">
        {items.map((p) => (
          <FailedPassage
            key={p.part_id}
            chapterID={chapterID}
            partID={p.part_id}
            text={p.text}
            failure={p.failure}
            unsaidStart={p.unsaid_start}
            unsaidEnd={p.unsaid_end}
            retryable={p.retryable}
            message={p.message}
          />
        ))}
      </ul>
      {retryable > 0 && (
        <button
          type="button"
          data-testid="problem-retry"
          disabled={retry.isPending || retry.isSuccess}
          onClick={() => retry.mutate()}
          className="mt-3 rounded-control border border-line px-3 py-1.5 text-sm disabled:opacity-40"
        >
          {t.narration.problemTryAgain(retryable)}
        </button>
      )}
      <MutationError
        error={retry.error}
        fallback={t.narration.startFailed}
        className="mt-1 text-xs"
      />
    </li>
  );
}

/** Problems by chapter, keeping the server's reading order. */
function byChapter(problems: NarrationProblem[]) {
  const out: {
    chapterID: string;
    title: string;
    failed: boolean;
    items: NarrationProblem[];
  }[] = [];
  for (const p of problems) {
    let last = out[out.length - 1];
    if (!last || last.chapterID !== p.chapter_id) {
      last = {
        chapterID: p.chapter_id,
        title: p.chapter_title,
        failed: p.render_status === "failed",
        items: [],
      };
      out.push(last);
    }
    last.items.push(p);
  }
  return out;
}
